/**
 * shipmentDeadline — Regras puras de prazo e duração da remessa.
 *
 * Compara a previsão de entrega (coluna DATE, sem fuso) com hoje ou com a
 * data de entrega, em dias de calendário, para a faixa do modal de detalhes.
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { parseDateOnly } from '@/lib/utils/date-only';

export type ShipmentDeadlineTone = 'success' | 'warning' | 'destructive';

export interface ShipmentDeadlineInfo {
  tone: ShipmentDeadlineTone;
  text: string;
  /** Dias restantes (positivo) ou em atraso (negativo). */
  days: number;
}

/** Situações em que não faz sentido exibir prazo. */
const NO_DEADLINE = ['cancelled', 'lost', 'returned'];

/** Meia-noite local do dia informado. */
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayDiff = (a: Date, b: Date) => Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86400000);
const plural = (n: number) => `${n} ${n === 1 ? 'dia' : 'dias'}`;

/** Calcula a faixa de prazo; null quando não há previsão ou a situação não se aplica. */
export function getShipmentDeadline(
  expected: string | null | undefined,
  deliveredAt: string | null | undefined,
  status: string,
  now: Date = new Date(),
): ShipmentDeadlineInfo | null {
  const due = parseDateOnly(expected ?? null);
  if (!due || NO_DEADLINE.includes(status)) return null;

  if (deliveredAt) {
    const late = dayDiff(new Date(deliveredAt), due);
    return late > 0
      ? { tone: 'destructive', text: `Entregue com ${plural(late)} de atraso`, days: -late }
      : { tone: 'success', text: 'Entregue no prazo', days: -late };
  }

  const left = dayDiff(due, now);
  if (left < 0) return { tone: 'destructive', text: `Atrasada há ${plural(-left)}`, days: left };
  if (left === 0) return { tone: 'warning', text: 'Prevista para hoje', days: 0 };
  return { tone: 'success', text: `Faltam ${plural(left)}`, days: left };
}

/** Duração da postagem até a entrega (ou até agora), ex.: "3d 4h". Null sem envio. */
export function formatShipmentDuration(
  shippedAt: string | null | undefined,
  deliveredAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!shippedAt) return null;
  const end = deliveredAt ? new Date(deliveredAt) : now;
  const min = Math.max(0, Math.floor((end.getTime() - new Date(shippedAt).getTime()) / 60000));
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}
